import React from 'react';
import { Badge, Chip, Tooltip } from '@mui/material';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { selectedIcon } from 'assets/theme/functions/boxShadow';
import { FileTypes, getIconLetter } from 'components/Lists/FilesList';
import { useMaterialUIController } from 'context';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

/**
 * FilesItem represents a single item (or a group of items) in the FilesList component, displaying
 * either an icon or a label (chips) based on the `iconLetterDisplayed` prop. It supports interaction through
 * clicking, which can either directly open a file if there's only one, or trigger a selection menu
 * for multiple files of the same type.
 *
 * Props:
 * @param {Object[]} files - An array of file objects associated with this item. Each file object
 *                           must include `contentId`, `label`, `type`, and may optionally include
 *                           `shortLabel`, `tooltip`, and `color`.
 * @param {boolean} [iconLetterDisplayed=false] - Determines whether an icon or a label is displayed
 *                                                for the file item(s). If true, an icon determined by
 *                                                the file type is displayed.
 * @param {string} selectedId - The ID of the file that is currently selected, if any. This is used
 *                              to apply selected styling to the item.
 * @param {function} handleIconClick - Callback function to be called when the file item is clicked.
 *                                     This function is responsible for handling the logic to display
 *                                     multiple files of the same type or directly opening a file.
 * @param {function} handleOnFileItemClick - Callback function that is called when a specific file
 *                                           item is selected from the menu of multiple files. This
 *                                           function is responsible for opening the selected file.
 *
 * Internally, the component decides to render either a `Badge` with an `MDButton` or a `Chip`,
 * based on the `iconLetterDisplayed` prop. The `Badge` component is used to show the number of
 * files when more than one file of the same type is present. The tooltip provides additional
 * context to the user, either showing the number of files or the file's label.
 *
 */
function FilesItem({
  files,
  iconLetterDisplayed,
  selectedId,
  handleIconClick,
  handleOnFileItemClick
}) {
  const {
    controller: { primaryColor, darkMode }
  } = useMaterialUIController();
  const { formatMessage: msg } = useIntl();
  const file = files[0];
  const { label, shortLabel, tooltip, color, type } = file;
  const colorType = color || getIconColor(type);
  const tooltipLabel = getTooltipLabel(tooltip, label, files?.length);
  const fileSelected = files.some((f) => f.contentId === selectedId);

  return (
    <Tooltip title={tooltipLabel}>
      {iconLetterDisplayed ? (
        <Badge
          badgeContent={files.length > 1 ? files.length : null}
          color={colorType}
          sx={{ '& .MuiBadge-badge': { top: 5 } }}
        >
          <MDButton
            sx={
              fileSelected
                ? {
                    minWidth: '10px',
                    padding: '0 10px 0px 10px',
                    boxShadow: selectedIcon(primaryColor, darkMode)
                  }
                : {
                    minWidth: '10px',
                    padding: '0 10px 0px 10px'
                  }
            }
            color="light"
            size="small"
            onClick={(event) => handleIconClick(event, files)}
          >
            <MDTypography color={colorType} fontWeight="bold" sx={{ fontSize: '13px' }}>
              {shortLabel || getIconLetter(file.type)}
            </MDTypography>
          </MDButton>
        </Badge>
      ) : (
        <Chip id={file.contentId} label={label} onClick={() => handleOnFileItemClick(file)} />
      )}
    </Tooltip>
  );

  function getTooltipLabel(tooltip, label, fileCount) {
    if (fileCount && fileCount > 1) return msg({ id: 'file.count.tooltip' }, { fileCount });
    return tooltip !== label ? tooltip ?? label : '';
  }

  function getIconColor(type) {
    if (type === FileTypes.Report) {
      return 'black';
    }
    if (type === FileTypes.Delib) {
      return 'primary';
    }
    return 'secondary';
  }
}

FilesItem.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      contentId: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      shortLabel: PropTypes.string,
      type: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
      color: PropTypes.string
    })
  ).isRequired,
  iconLetterDisplayed: PropTypes.bool,
  selectedId: PropTypes.string,
  handleIconClick: PropTypes.func.isRequired,
  handleOnFileItemClick: PropTypes.func.isRequired
};

FilesItem.defaultProps = {
  iconLetterDisplayed: false,
  selectedId: null
};

export default FilesItem;
